import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { PageLoader } from "@/components/ui/loader";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Lock, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useConfirmRun, useDeleteEntry, useDepLines, useDepRun, useLockRun } from "@/features/finance/api";
import { DepStatusBadge } from "@/features/finance/badges";
import { inr } from "@/features/finance/format";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useBranch } from "@/context/BranchContext";
import { ReportExportButton } from "@/features/reports/export/ReportExportButton";

export const Route = createFileRoute("/_authenticated/finance/depreciation/$runId")({
  component: DepRunDetail,
});

function DepRunDetail() {
  const { runId }  = useParams({ from: "/_authenticated/finance/depreciation/$runId" });
  const navigate   = useNavigate();
  const { data: me } = useCurrentUser();
  const { currentBranchId } = useBranch();
  const { data: run } = useDepRun(runId);
  const { data: lines } = useDepLines(runId, currentBranchId);
  const confirm    = useConfirmRun();
  const lock       = useLockRun();
  const deleteEntry = useDeleteEntry();

  const canEdit    = !!me?.roles.some((r) => r === "super_admin" || r === "finance");
  const isConfirmed = run?.status === "Confirmed";
  const isLocked   = run?.status === "Locked";
  const totalDep   = (lines ?? []).reduce((s, r) => s + Number(r.dep_amount ?? 0), 0);

  if (!run) return <PageLoader />;

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/finance/depreciation" })}>
        <ArrowLeft className="mr-1 h-4 w-4" />Back
      </Button>

      {/* Run Summary Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>{run.run_name}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              FY {run.fy} · {run.period} · {run.method} · FINANCIAL
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <DepStatusBadge status={run.status} />

            {/* Confirm (Draft → Confirmed) */}
            {canEdit && run.status === "Draft" && (
              <Button
                size="sm"
                variant="outline"
                disabled={confirm.isPending}
                onClick={async () => {
                  try {
                    const n = await confirm.mutateAsync(run.id);
                    toast.success(`Confirmed — ${n} entries created`);
                  } catch (e) { toast.error((e as Error).message); }
                }}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" />Confirm
              </Button>
            )}

            {/* Re-confirm (Confirmed → re-materialise entries) */}
            {canEdit && isConfirmed && (
              <Button
                size="sm"
                variant="outline"
                disabled={confirm.isPending}
                onClick={async () => {
                  if (!window.confirm("Re-run calculations? Existing entries will be replaced.")) return;
                  try {
                    const n = await confirm.mutateAsync(run.id);
                    toast.success(`Re-confirmed — ${n} entries recreated`);
                  } catch (e) { toast.error((e as Error).message); }
                }}
              >
                <CheckCircle2 className="mr-1 h-4 w-4" />Re-confirm
              </Button>
            )}

            {/* Lock (Confirmed → Locked) */}
            {canEdit && isConfirmed && (
              <Button
                size="sm"
                disabled={lock.isPending}
                onClick={async () => {
                  if (!window.confirm("Lock this run? This posts depreciation to all assets and cannot be undone.")) return;
                  try {
                    await lock.mutateAsync(run.id);
                    toast.success("Run locked — depreciation posted to assets");
                  } catch (e) { toast.error((e as Error).message); }
                }}
              >
                <Lock className="mr-1 h-4 w-4" />Lock
              </Button>
            )}

            {/* Export */}
            {(lines ?? []).length > 0 && (
              <ReportExportButton
                reportKey="DEP-RUN"
                title={run.run_name ?? "Depreciation Run"}
                filename={`dep-run-${run.fy}-${run.period}`}
                rows={() => (lines ?? []).map((l) => ({
                  "Asset Code":   l.asset_code,
                  "Asset Name":   l.asset_name,
                  "Category":     l.category_name ?? "",
                  "Method":       l.depreciation_method ?? "",
                  "Rate (%)":     Number(l.dep_rate ?? 0),
                  "Days":         l.days_used,
                  "Opening NBV":  Number(l.opening_nbv ?? 0),
                  "Dep Amount":   Number(l.dep_amount ?? 0),
                  "Closing NBV":  Number(l.closing_nbv ?? 0),
                }))}
                requiredColumns={["Asset Code"]}
                variant="outline"
                size="sm"
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-6 text-sm">
            <span>
              Total Depreciation:{" "}
              <span className="font-semibold text-orange-600 dark:text-orange-400">{inr(totalDep)}</span>
            </span>
            <span>
              Entries: <span className="font-semibold">{(lines ?? []).length}</span>
            </span>
            {isLocked && (
              <Badge variant="destructive" className="text-xs">
                Locked — read-only
              </Badge>
            )}
            {isConfirmed && !isLocked && (
              <Badge variant="outline" className="text-xs border-orange-400 text-orange-600">
                Entries editable before lock
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Depreciation Lines
            {isConfirmed && !isLocked && (
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                You can delete individual entries before locking.
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-[65vh] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Asset</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Rate %</TableHead>
                  <TableHead className="text-right">Opening NBV</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Dep Amount</TableHead>
                  <TableHead className="text-right">Closing NBV</TableHead>
                  {isConfirmed && !isLocked && <TableHead />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(lines ?? []).map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="font-mono text-xs">{l.asset_code}</TableCell>
                    <TableCell>{l.asset_name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{l.category_name ?? "—"}</TableCell>
                    <TableCell>
                      {l.depreciation_method ? (
                        <Badge variant={l.depreciation_method === "SLM" ? "secondary" : "outline"} className="text-xs">{l.depreciation_method}</Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {Number(l.dep_rate ?? 0).toFixed(2)}%
                    </TableCell>
                    <TableCell className="text-right">{inr(l.opening_nbv ?? 0)}</TableCell>
                    <TableCell className="text-right">{l.days_used ?? "—"}</TableCell>
                    <TableCell className="text-right font-medium text-orange-600 dark:text-orange-400">
                      {inr(l.dep_amount ?? 0)}
                    </TableCell>
                    <TableCell className="text-right">{inr(l.closing_nbv ?? 0)}</TableCell>
                    {isConfirmed && !isLocked && (
                      <TableCell>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          disabled={deleteEntry.isPending}
                          onClick={async () => {
                            if (!window.confirm("Remove this entry from the run?")) return;
                            try {
                              await deleteEntry.mutateAsync({ entryId: l.id, runId });
                              toast.success("Entry removed");
                            } catch (e) { toast.error((e as Error).message); }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {(lines ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isConfirmed && !isLocked ? 10 : 9} className="text-center text-muted-foreground py-10">
                      {run.status === "Draft"
                        ? "No entries yet. Click Confirm to calculate and materialise entries."
                        : "No entries in this run."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
